import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';

interface Session {
  id: string;
  session_num: number;
  phase: string;
  scheduled_at: string;
  status: string;
  energy_score: number | null;
  trainer_notes: string | null;
}

interface DocumentRelease {
  id: string;
  document_id: string;
  stage: number;
  released_at: string;
  accessed_at: string | null;
}

interface MicroAction {
  id: string;
  session_id: string;
  text: string;
  status: string;
  due_date: string;
}

interface EnrolmentDetail {
  id: string;
  programme_type: string;
  package: string;
  status: string;
  current_phase: string;
  current_session: number;
  start_date: string;
  trainer_name?: string;
  sessions: Session[];
  documents: DocumentRelease[];
  micro_actions: MicroAction[];
}

const PHASE_LABELS: Record<string, string> = {
  S: 'See Yourself Clearly',
  P: 'Plant New Roots',
  A: 'Arm Yourself',
  R: 'Rise to the Room',
  K: 'Keep the Flame',
  E1: 'Explore 1',
  E2: 'Explore 2',
  D: 'Decide',
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-gray-100 text-gray-600',
  psq_submitted: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  complete: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

export function Programme() {
  usePageTitle('My Programme');
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<EnrolmentDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      api.get<EnrolmentDetail>(`/programmes/${id}`)
        .then(setData)
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) return <Layout><div className="max-w-4xl mx-auto mt-20 px-6 text-center"><p className="text-mustard-charcoal/60">Loading programme...</p></div></Layout>;
  if (!data) return <Layout><div className="max-w-4xl mx-auto mt-20 px-6 text-center"><p className="text-red-600">Programme not found.</p></div></Layout>;

  const programmeLabel = data.programme_type === 'lifespark360' ? 'LifeSpark360' : 'Seedbed';
  const pendingActions = data.micro_actions.filter((a) => a.status === 'pending');

  return (
    <Layout>
      <div className="max-w-5xl mx-auto mt-10 px-6 pb-20">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <p className="text-sm tracking-widest text-mustard-green-mid uppercase font-bold mb-1">{programmeLabel}</p>
            <h1 className="font-display text-3xl font-bold text-mustard-green-dark">Your Programme</h1>
            <p className="text-mustard-charcoal/60 mt-1">Phase {data.current_phase} — {PHASE_LABELS[data.current_phase] || data.current_phase}</p>
          </div>
          <div className={`px-4 py-2 rounded-lg font-medium text-sm ${data.status === 'active' ? 'bg-green-100 text-green-700' : data.status === 'completed' ? 'bg-mustard-gold/20 text-mustard-gold' : 'bg-gray-100 text-gray-600'}`}>
            {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sessions Timeline */}
          <div className="lg:col-span-2">
            <h2 className="font-display text-xl font-bold text-mustard-green-dark mb-4">Sessions</h2>
            <div className="space-y-3">
              {data.sessions.map((session) => (
                <div key={session.id} className={`bg-white rounded-xl p-5 shadow-sm border border-gray-100 ${session.status === 'complete' ? 'opacity-80' : ''}`}>
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${session.status === 'complete' ? 'bg-mustard-green-dark text-white' : 'bg-gray-100 text-mustard-charcoal'}`}>
                        {session.session_num}
                      </div>
                      <div>
                        <p className="font-medium text-mustard-charcoal">
                          Session {session.session_num} — Phase {session.phase}
                        </p>
                        <p className="text-xs text-mustard-charcoal/50">
                          {session.scheduled_at ? new Date(session.scheduled_at).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'Not scheduled'}
                        </p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[session.status] || 'bg-gray-100'}`}>
                      {session.status.replace('_', ' ')}
                    </span>
                  </div>
                  {session.status === 'scheduled' && data.status === 'active' && (
                    <div className="mt-3">
                      <Link to={`/programme/${data.id}/psq/${session.id}`} className="text-sm text-mustard-green font-medium hover:underline">
                        Submit Pre-Session Questionnaire
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pending Micro-Actions */}
            {pendingActions.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-3">Micro-Actions</h3>
                <div className="space-y-2">
                  {pendingActions.map((action) => (
                    <div key={action.id} className="flex items-start gap-2">
                      <button
                        onClick={() => {
                          api.patch(`/programmes/micro-actions/${action.id}`, { status: 'completed' })
                            .then(() => setData((d) => d ? { ...d, micro_actions: d.micro_actions.map((a) => a.id === action.id ? { ...a, status: 'completed' } : a) } : d));
                        }}
                        className="mt-1 w-5 h-5 rounded border-2 border-mustard-green-mid flex-shrink-0 hover:bg-mustard-green-mid/20 transition-colors"
                      />
                      <div>
                        <p className="text-sm text-mustard-charcoal">{action.text}</p>
                        <p className="text-xs text-mustard-charcoal/40">Due {new Date(action.due_date).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Documents */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <h3 className="font-display text-lg font-bold text-mustard-green-dark mb-3">Documents</h3>
              {data.documents.length === 0 ? (
                <p className="text-sm text-mustard-charcoal/50">No documents released yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between text-sm">
                      <span className="text-mustard-charcoal truncate mr-2">
                        {doc.document_id.replace(/^(LS360|SB)-\d+-/, '').replace(/-/g, ' ')}
                      </span>
                      <span className="text-xs text-mustard-charcoal/40 flex-shrink-0">
                        Stage {doc.stage}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
